import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import logdna from '@logdna/browser';
import { Store } from '@ngrx/store';
import _ from 'lodash';
import { ModalDirective, ToastService } from 'ng-uikit-pro-standard';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { EmailValidator } from '../../shared/helpers/email-validator';
import { nullSafePath } from '../../shared/helpers/null-safe-path';
import { SessionService } from '../../shared/services/session.service';
import { selectAppApplicantSourceId, State } from '../../state';
import { ApplicantService } from '../applicant-service/applicant.service';
import { Application } from '../applicant-service/service-models/Application';
import { IterativeApplicationRouterService } from '../iterative-application-router/iterative-application-router.service';
import { JobService } from '../job-service/job.service';
import { JobStatusModel } from '../job-service/models/job-status-model';
import { ApplicantElementViewed } from '../job-service/service-models/ApplicantElementViewed';
import { ResumeModel } from '../job-service/service-models/ResumeModel';
import { JobStatusService } from '../job-status.service';

@Component({
  selector: 'app-apply-resume',
  templateUrl: './apply-resume.component.html',
  styleUrls: ['./apply-resume.component.scss']
})
export class ApplyResumeComponent implements OnInit, OnDestroy {
  private subscription: Subscription = new Subscription();
  private identifier: string;
  private subBrandIdentifier: string;
  model: ResumeModel;
  coverLetter: File;
  resume: File;
  applicantId: string;
  coverLetterName: string;
  resumeName: string;
  application = new Application();
  coverLetterFileIsEmpty: boolean;
  resumeFileIsEmpty: boolean;
  resumeFileIsTooBig: boolean;
  resumeFileTypeIsNotSupported: boolean;
  coverLetterFileIsTooBig: boolean;
  coverLetterFileTypeIsNotSupported: boolean;
  processingServerSideRequest: boolean;
  uploadingResume: boolean;
  uploadedResume: boolean;
  resumeInvalid: boolean;
  uploadingCoverLetter: boolean;
  uploadedCoverLetter: boolean;
  coverLetterInvalid: boolean;
  @ViewChild('areYouSureModal') areYouSureModal: ModalDirective;
  @ViewChild('resumeFileInput') resumeInput: ElementRef;
  @ViewChild(NgForm) resumeForm: NgForm;
  showAreYouSureModal = true;
  emailRegex: string;

  constructor(private route: ActivatedRoute,
    private router: Router,
    private jobService: JobService,
    private applicantService: ApplicantService,
    private jobStatusService: JobStatusService,
    private iterativeApplicationRouterService: IterativeApplicationRouterService,
    private readonly sessionService: SessionService,
    private readonly store: Store<State>,
    private readonly toastService: ToastService) {
    this.resumeName = '';
    this.processingServerSideRequest = false;
    this.uploadingResume = false;
    this.uploadedResume = false;
    this.uploadingCoverLetter = false;
    this.uploadedCoverLetter = false;
  }

  ensureCanReplaceSameDocument($event) {
    $event.target.value = null;
  }
  
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit() {
    const emailValidator = new EmailValidator();
    this.emailRegex = emailValidator.emailRegex;

    this.subscription.add(this.route.data
      .subscribe((data: { model: ResumeModel, status: JobStatusModel }) => {
        this.model = data.model;

        if (this.model.applicant !== undefined && this.model.applicant !== null) {
          this.application.firstName = this.model.applicant.firstName;
          this.application.lastName = this.model.applicant.lastName;
          this.application.emailAddress = this.model.applicant.emailAddress;
          this.application.resumeLocation = this.model.applicant.resumeName;
          this.application.coverLetterLocation = this.model.applicant.coverLetterName;

          this.uploadedResume = this.model.applicant.resumeName !== ''
            && this.model.applicant.resumeName !== null;
          this.uploadedCoverLetter = this.model.applicant.coverLetterName !== ''
            && this.model.applicant.coverLetterName !== null;

          if (this.uploadedResume) {
            this.resumeName = this.model.applicant.resumeName.split('/').pop();
          }

          if (this.uploadedCoverLetter) {
            this.coverLetterName = this.model.applicant.coverLetterName.split('/').pop();
          }
        }
        
        this.jobStatusService.checkIfPageAccessible(data.status, true);
      }));

    this.subscription.add(this.route.paramMap.subscribe((params: ParamMap) => {
      this.identifier = params.get('identifier');
      this.subBrandIdentifier = params.get('subBrandIdentifier');
      this.application.applicantId = this.applicantId = params.get('applicantId');
      this.application.jobId = +params.get('jobId');
      this.application.sessionId = this.sessionService.getSessionId();
      this.jobService.trackApplicantEvent(this.application.jobId, new ApplicantElementViewed(
        this.application.jobId, new Date(), this.sessionService.getSessionId(),
        this.model.resume.id, this.model.resume.version, this.model.resume.sortOrder)).subscribe();
    }));

    this.subscription.add(this.route.queryParamMap.subscribe(queryParams => {
      const token = queryParams.get('token');
      if (token !== undefined && token !== null && token !== '') {
        this.application.token = token;
      } else {
        this.store.select(selectAppApplicantSourceId)
          .pipe(take(1), filter(x => x !== null))
          .subscribe(x => this.application.sourceId = x);
      }
    }));
    
    if (this.model.applicant?.submittedOn) {
      this.router.navigateByUrl(`${this.router.url.toString()
        .split('/jobs/')[0]}/jobs/${this.application.jobId}/apply/${this.application.applicantId}/part-one-already-completed-thank-you`);
    }
  }

  removeCoverLetter() {
    this.subscription.add(
      this.applicantService.deleteCoverLetter(this.application.jobId, this.applicantId, this.coverLetterName)
        .subscribe(data => {
          this.uploadingCoverLetter = false;
          this.uploadedCoverLetter = false;
          this.coverLetterInvalid = false;
          this.coverLetter = this.coverLetterName = this.application.coverLetterLocation = null;
        },
          error => {
          }
        ));
  }

  removeResume() {
    if (!environment().development) {
      logdna.info('Missing Resume: Removing resume', {
        ApplicantId: this.applicantId,
        JobId: this.application.jobId,
        ResumeName: this.resume?.name,
        ResumeSize: this.resume?.size
      });
    }
    this.subscription.add(
      this.applicantService.deleteResume(this.application.jobId, this.applicantId, this.resumeName)
      .subscribe(data => {
        if (!environment().development) {
          logdna.info('Missing Resume: Successfully deleted resume', {
            ApplicantId: this.applicantId,
            JobId: this.application.jobId,
            ResumeName: this.resume?.name,
            ResumeSize: this.resume?.size
          });
        }
      
        this.uploadingResume = false;
        this.uploadedResume = false;
        this.resumeInvalid = false;
        this.resume = this.resumeName = this.application.resumeLocation = null;
      },
      error => {
        if (!environment().development) {
          logdna.error('Missing Resume: Failed to delete resume', {
            ApplicantId: this.applicantId,
            JobId: this.application.jobId,
            ResumeName: this.resume?.name,
            ResumeSize: this.resume?.size
          });
        }
      }
    ));
  }

  save() {
    if (this.processingServerSideRequest) {
      return;
    }

    this.processingServerSideRequest = true;

    if (!environment().development) {
      logdna.info('Missing Resume: Saving and continuing. Creating/updating application.', {
        ApplicantId: this.applicantId,
        JobId: this.application.jobId,
        ResumeName: this.resume?.name,
        ResumeSize: this.resume?.size
      });
    }
    
    this.subscription.add(this.applicantService.createApplication(this.application)
      .subscribe(
        data => {
          const segments = data.headers.get('Location').split('/');
          const applicationId = Number.parseInt(segments[segments.length - 1]);

          localStorage.setItem('applicationId', applicationId.toString());
          const resumeId = _.find(this.model.yellowBrickRoad.orderableElements, x => x.type === 'Resume').id;

          this.iterativeApplicationRouterService.next(
            nullSafePath(this.identifier, this.subBrandIdentifier),
            this.model.yellowBrickRoad,
            resumeId,
            this.application.jobId,
            this.applicantId,
            true,
            0);
          
      },
      error => {
        this.processingServerSideRequest = false;
      }));
  }

  uploadCoverLetter($event) {
    this.coverLetter = null;
    this.coverLetterName = null;
    this.uploadedCoverLetter = false;
    const file = $event.srcElement.files[0];
    this.coverLetterFileTypeIsNotSupported = !this.isValidFileType(file.name);
    this.coverLetterFileIsTooBig = !this.isValidFileSize(file.size);
    this.coverLetterFileIsEmpty = file.size === 0;
    if (this.coverLetterFileIsTooBig || this.coverLetterFileIsEmpty || this.coverLetterFileTypeIsNotSupported) {
      this.coverLetterInvalid = true;
      return;
    }

    this.coverLetterInvalid = false;
    this.coverLetter = file;
    this.coverLetterName = this.coverLetter.name;

    this.processingServerSideRequest = true;
    this.uploadingCoverLetter = true;
    this.uploadedCoverLetter = false;

    this.subscription.add(this.applicantService.uploadCoverLetter(this.application.jobId, this.applicantId, this.coverLetter).subscribe(
      data => {
        this.application.coverLetterLocation = data.location;
        this.processingServerSideRequest = false;
        this.uploadingCoverLetter = false;
        this.uploadedCoverLetter = true;
      },
      error => {
        this.processingServerSideRequest = false;
        this.uploadingCoverLetter = false;
        this.coverLetter = null;
        this.resumeForm.controls['coverLetterValue'].setValue('');
        this.toastService.error('There was a problem uploading your file. Please try again.', 'Error!');
      }
    ));
  }

  uploadResume($event) {
    this.resume = null;
    this.resumeName = null;
    this.uploadedResume = false;
    const file = $event.srcElement.files[0];
    this.resumeFileTypeIsNotSupported = !this.isValidFileType(file.name);
    this.resumeFileIsTooBig = !this.isValidFileSize(file.size);
    this.resumeFileIsEmpty = file.size === 0;
    if (this.resumeFileTypeIsNotSupported || this.resumeFileIsEmpty || this.resumeFileIsTooBig) {
      this.resumeInvalid = true;
      return;
    }

    this.resumeInvalid = false;
    this.resume = $event.target.files[0];
    this.resumeName = this.resume.name;

    this.processingServerSideRequest = true;
    this.uploadingResume = true;
    this.uploadedResume = false;

    if (!environment().development) {
      logdna.info('Missing Resume: Uploading resume', {
        ApplicantId: this.applicantId,
        JobId: this.application.jobId,
        ResumeName: this.resume?.name,
        ResumeSize: this.resume?.size
      });
    }
    
    this.subscription.add(this.applicantService.uploadResume(this.application.jobId, this.applicantId, this.resume).subscribe(
      data => {
        if (!environment().development) {
          logdna.info('Missing Resume: Successfully uploaded resume', {
            ApplicantId: this.applicantId,
            JobId: this.application.jobId,
            ResumeName: this.resume?.name,
            ResumeSize: this.resume?.size
          });
        }
        this.application.resumeLocation = data.location;
        this.processingServerSideRequest = false;
        this.uploadingResume = false;
        this.uploadedResume = true;
      },
      error => {
        if (!environment().development) {
          logdna.error('Missing Resume: Failed to upload resume', {
            ApplicantId: this.applicantId,
            JobId: this.application.jobId,
            ResumeName: this.resume?.name,
            ResumeSize: this.resume?.size
          });
        }
        this.processingServerSideRequest = false;
        this.uploadingResume = false;
        this.resume = null;
        this.resumeForm.controls['fileValue'].setValue('');
        this.toastService.error('There was a problem uploading your file. Please try again.', 'Error!');
      }
    ));
  }

  openResumeControl($event) {
    if (this.showAreYouSureModal
      && this.model.applicant !== undefined
      && this.model.applicant !== null) {
      this.areYouSureModal.show();
      $event.stopPropagation();
      return false;
    } else {
      this.ensureCanReplaceSameDocument($event);
    }
  }

  openUploadResumeControl() {
    this.showAreYouSureModal = false;
    this.areYouSureModal.hide();
    const inputElement: HTMLElement = this.resumeInput.nativeElement as HTMLElement;
    inputElement.click();
    setTimeout(() => this.showAreYouSureModal = true, 0);
  }

  private isValidFileSize(size: number): boolean {
    const maxFileSize = 5242880;
    
    return maxFileSize >= size;
  }

  private isValidFileType(filename: string): boolean {
    const supportedFileTypes = [ '.doc', '.docx', '.pdf', '.rtf', '.txt' ];
    const fileExtension = filename.substring(filename.lastIndexOf('.')).toLocaleLowerCase();

    return supportedFileTypes.some(x => x === fileExtension);
  }
}
